import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useStoreByName } from "../../../hooks/use-stores.hooks";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ROLES } from "@shared/types";
import { clearAuthData } from "../../../services/api/axios-instance";

interface AdminGuardProps {
  children: React.ReactNode;
}

/**
 * רכיב המגן על דפי האדמין מפני גישה לא מורשית
 * מבצע בדיקה אם למשתמש יש הרשאת אדמין או מנהל בית ספר ואם לא, מנווט אותו לדף הבית
 */
const AdminGuard = observer(({ children }: AdminGuardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userStore = useStoreByName("userStore");

  // בדיקה אם המשתמש מחובר והאם יש לו הרשאת אדמין או מנהל בית ספר
  const isLoggedIn = !!userStore.userData;
  const userRoleId = userStore.userData?.personalInfo?.role?.id;
  const isAdmin = userRoleId === ROLES.ADMIN;
  const isPrincipal = userRoleId === ROLES.PRINCIPAL;
  const hasAdminAccess = isAdmin || isPrincipal;

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    // שליפת הטוקן מהאחסון המקומי
    const token = localStorage.getItem("access_token");

    // אם אין טוקן, נווט לדף האימות ונקה את נתוני האימות
    if (!token) {
      clearAuthData();
      navigate("/", { replace: true });
      return;
    }

    // אם יש טוקן אבל אין מידע על המשתמש, נסה לקבל מידע
    if (token && !userStore.userData) {
      
      // הגדרת timeout של 10 שניות למקרה שהשרת לא מגיב
      timeoutId = setTimeout(() => {
        console.error("AdminGuard: Timeout while fetching user data");
        clearAuthData();
        navigate("/", { replace: true });
      }, 10000);
      
      userStore.getUserData()
        .then(() => {
          clearTimeout(timeoutId);
        })
        .catch((error: unknown) => {
          clearTimeout(timeoutId);
          console.error("AdminGuard: Failed to fetch user data:", error);
          // נקה את נתוני האימות ונווט לדף האימות
          clearAuthData();
          navigate("/", { replace: true });
        });
      return;
    }

    // אם המשתמש מחובר אבל אין לו הרשאת אדמין או מנהל בית ספר, נווט לדף הבית
    if (isLoggedIn && !hasAdminAccess) {
      navigate("/home", { replace: true });
    }
    
    // ניקוי timeout כאשר הקומפוננטה מתפרקת
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [navigate, userStore, isLoggedIn, hasAdminAccess, userRoleId]);

  // אם המשתמש מחובר ויש לו הרשאת אדמין או מנהל בית ספר, הצג את תוכן הדף
  if (isLoggedIn && hasAdminAccess) {
    return children;
  }

  // בזמן בדיקת ההרשאות, הצג מסך טעינה
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
      }}
    >
      <CircularProgress size={60} />
      <Typography variant="h6" sx={{ mt: 2 }}>
        {t("checkingPermissions")}
      </Typography>
    </Box>
  );
});

export default AdminGuard;
